import SignClient from "@walletconnect/sign-client";
import { Web3Modal } from "@web3modal/standalone";
import { SigningStargateClient } from "@cosmjs/stargate";
import { TxRaw } from "cosmjs-types/cosmos/tx/v1beta1/tx";
import { fromBase64 } from "@cosmjs/encoding";
import * as web3 from "@solana/web3.js";
import bs58 from "bs58";

import { extendedRegistryTypes } from "../config/registry";
import { aminoTxToSignDoc } from "../lib/tendermint";
import { coins } from "../constants";

const chansMap = {
  ethereum: "eip155:1",
  cosmos: "cosmos:cosmoshub-4",
  solana: "solana:4sGjMW1sUnHzSxGspuhpqLDx6wiyjNtZ",
};

const namespacesMap = {
  ethereum: {
    methods: ["eth_signTransaction", "eth_sendTransaction"],
    chains: ["eip155:1"],
    events: [],
  },
  cosmos: {
    methods: ["cosmos_signDirect", "cosmos_signAmino", "cosmos_getAccounts"],
    chains: ["cosmos:cosmoshub-4"],
    events: [],
  },
  solana: {
    methods: ["solana_signTransaction"],
    chains: ["solana:4sGjMW1sUnHzSxGspuhpqLDx6wiyjNtZ"],
    events: [],
  },
};

const signMethodsMap = {
  ethereum: async ({ signClient, rawTx, topic, chainId }) => {
    const txHash = await signClient.request({
      topic,
      chainId,
      request: {
        jsonrpc: "2.0",
        method: "eth_sendTransaction",
        params: [rawTx],
      },
    });
    return { txHash };
  },
  cosmos: async ({
    signClient,
    rawTx,
    topic,
    chainId,
    accountAddress,
    accountPayload,
    accountPubkey,
  }) => {
    const stargateClient = await SigningStargateClient.offline();
    registerRegistry(stargateClient.registry, extendedRegistryTypes);
    const { signDoc } = aminoTxToSignDoc(rawTx, stargateClient, {
      ...accountPayload,
      pubkeyBytes: Buffer.from(accountPubkey, "base64"),
    });
    const { signature } = await signClient.request({
      topic,
      chainId,
      request: {
        jsonrpc: "2.0",
        method: "cosmos_signDirect",
        params: {
          signerAddress: accountAddress,
          signDoc: {
            chainId: signDoc.chainId,
            accountNumber: rawTx.account_number,
            authInfoBytes: Buffer.from(signDoc.authInfoBytes).toString("hex"),
            bodyBytes: Buffer.from(signDoc.bodyBytes).toString("hex"),
          },
        },
      },
    });
    const txRawPartial = TxRaw.fromPartial({
      bodyBytes: signDoc.bodyBytes,
      authInfoBytes: signDoc.authInfoBytes,
      signatures: [fromBase64(signature)],
    });
    const txBytes = TxRaw.encode(txRawPartial).finish();
    return {
      tx_bytes: Array.from(txBytes),
    };
  },
  solana: async ({ signClient, rawTx, topic, chainId }) => {
    const { signature } = await signClient.request({
      topic,
      chainId,
      request: {
        jsonrpc: "2.0",
        method: "solana_signTransaction",
        params: {
          feePayer: rawTx.feePayer.toBase58(),
          instructions: rawTx.instructions.map((i) => ({
            programId: i.programId.toBase58(),
            data: bs58.encode(i.data),
            keys: i.keys.map((k) => ({
              isSigner: k.isSigner,
              isWritable: k.isWritable,
              pubkey: k.pubkey.toBase58(),
            })),
          })),
          recentBlockhash: rawTx.recentBlockhash,
          signatures:
            rawTx.signatures?.map((i) => ({
              publicKey: i.publicKey.toBase58(),
              signature: i.signature ? bs58.encode(i.signature) : i.signature,
            })) || rawTx.signatures,
        },
      },
    });
    rawTx.addSignature(rawTx.feePayer, Buffer.from(bs58.decode(signature)));
    const connection = new web3.Connection(process.env.VUE_APP_SOLANA_ENDPOINT);
    const result = await connection.sendRawTransaction(
      rawTx.serialize({ verifySignatures: false })
    );
    return { txHash: result };
  },
};

const Backend = ({
  coinName,
  signClient,
  accounts,
  chainId,
  topic,
  sign,
  accountPubkey,
}) => {
  const getAccount = async () => {
    const accountPayload = coins[coinName];
    return Account({
      signClient,
      accounts,
      chainId,
      topic,
      sign,
      accountPayload,
      accountPubkey,
    });
  };

  return {
    getAccount,
  };
};

const Account = ({
  signClient,
  accounts,
  chainId,
  topic,
  sign,
  accountPayload,
  accountPubkey,
}) => {
  const getAddress = async () => {
    return { address: accounts[0] };
  };
  const signTransaction = async (rawTx) => {
    return sign({
      signClient,
      rawTx,
      topic,
      chainId,
      accountPayload,
      accountAddress: (await getAddress()).address,
      accountPubkey,
    });
  };
  return {
    getAddress,
    signTransaction,
  };
};

Backend.create = async ({
  walletconnect: { projectId, network, accountAddress, accountPubkey },
  coinName,
}) => {
  const chainId = chansMap[network];
  const namespace = chainId.split(":")[0];
  const web3Modal = new Web3Modal({
    walletConnectVersion: 2,
    projectId,
    standaloneChains: [chainId],
  });
  let done = false;
  return new Promise(async (resolve, reject) => {
    const unsubscribe = web3Modal.subscribeModal((newState) => {
      if (!newState.open && !done) {
        reject(new Error("User closed modal"));
      }
    });
    try {
      const signClient = await SignClient.init({ projectId });
      let pairingTopic;
      if (accountAddress) {
        pairingTopic = getParingTopic(accountAddress);
      }
      const { uri, approval } = await signClient.connect({
        pairingTopic,
        requiredNamespaces: {
          [namespace]: namespacesMap[network],
        },
      });
      if (uri) {
        web3Modal.openModal({ uri, standaloneChains: [chainId] });
        const session = await approval();
        done = true;
        const accounts = getAccountsFromNamespace(
          session.namespaces[namespace]
        );
        const parings = signClient.pairing.getAll({ active: true });
        saveParingTopic(accounts[0], parings[0]?.topic);
        saveSessionTopic(accounts[0], session.topic);
        resolve(
          Backend({
            coinName,
            signClient,
            accounts,
            chainId,
            topic: session.topic,
            sign: signMethodsMap[network],
            accountPubkey,
          })
        );
      } else {
        resolve(
          Backend({
            coinName,
            signClient,
            accounts: [accountAddress],
            chainId,
            topic: getSessionTopic(accountAddress),
            sign: signMethodsMap[network],
            accountPubkey,
          })
        );
      }
    } catch (error) {
      reject(error);
    } finally {
      unsubscribe();
      web3Modal.closeModal();
    }
  });
};

const getAccountsFromNamespace = (namespaceInfo) => {
  return namespaceInfo.accounts.map((acc) => acc.split(":").slice(-1)[0]);
};

const saveParingTopic = (accountAddress, topic) => {
  if (!topic) return;
  sessionStorage.setItem(
    "wc_paring_topic:" + accountAddress.toLowerCase(),
    topic
  );
};
const getParingTopic = (accountAddress) => {
  return (
    sessionStorage.getItem("wc_paring_topic:" + accountAddress.toLowerCase()) ||
    undefined
  );
};

const saveSessionTopic = (accountAddress, topic) => {
  if (!topic) return;
  sessionStorage.setItem(
    "wc_session_topic:" + accountAddress.toLowerCase(),
    topic
  );
};
const getSessionTopic = (accountAddress) => {
  return (
    sessionStorage.getItem(
      "wc_session_topic:" + accountAddress.toLowerCase()
    ) || undefined
  );
};

const registerRegistry = (registry, types) => {
  Object.keys(types).forEach((type) => registry.register(type, types[type]));
};

export default Backend;
